import {
  Box,
  Card,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  CardContent,
  CardHeader,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Info,
  X,
  ChevronsUp as ScrollArrowsUp,
  ChevronsDown as ScrollArrowsDown
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import GetDailyBlockCounts from 'src/api/GetDailyBlockCounts';
import GetSubAccountFinancialsReport from 'src/api/GetSubAccountFinancialsReport';
import getHashrateUnit, { HASHRATE } from 'src/api/HashrateFormatter';
import TablePaginationComp from 'src/components/TablePaginationComp';
import { TableSortControls } from 'src/components/TableSortControls';
import useAuth from 'src/hooks/useAuth';
import { applySorting, getComparator } from 'src/utils/filterAndSortData';
import FPPSCalculationBreakdown from 'src/views/calculator/FPPSCalculationBreakdown';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  table: {
    minWidth: 650
  },
  calculationInfo: {
    fontSize: 14,
    cursor: 'pointer',
    color: 'rgb(96, 165, 250)',
    '&:hover': {
      color: 'rgb(147 197 253 / var(--tw-text-opacity, 1));'
    }
  },
  scrollButtonsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  hideScrollButton: {
    display: 'none'
  },
  topScrollButton: {
    display: 'flex',
    alignSelf: 'center',
    position: 'absolute',
    top: '72px',
    height: '50px',
    width: '50px',
    backgroundColor: 'var(--color-accent)',
    outline: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  },
  bottomScrollButton: {
    position: 'absolute',
    display: 'flex',
    alignSelf: 'center',
    bottom: '12px',
    height: '50px',
    width: '50px',
    backgroundColor: 'var(--color-accent)',
    outline: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  }
}));

const AuditReportTable = ({
  startDate,
  endDate,
  subAccounts,
  saveExportData
}) => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const { classes } = useStyles();
  const theme = useTheme();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [error, setError] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('startDate');
  const [showDifficultyChangeInfo, setShowDifficultyChangeInfo] = useState(
    false
  );
  const [selectedRow, setSelectedRow] = useState(null);
  var sortedRows = applySorting(rows, getComparator(order, orderBy));
  const [openFppsCalcDialog, setOpenFppsCalcDialog] = useState(false);
  const [selectedFppsDetailsDate, setSelectedFppsDetailsDate] = useState(null);
  const [selectorValues, setSelectorValues] = useState({
    clear: t('clear'),
    year: 'Year',
    startDate: `${t('startTime')} (UTC)`,
    endDate: `${t('endTime')} (UTC)`,
    groupName: `${t('group')}`,
    subAccountName: `${t('subAccount')}`,
    aggregateShareDifficulty: `${t('aggregateShareDifficulty')}`,
    convertedHashrate: `${t('hashrate')}`,
    networkDifficulty: `${t('networkDifficulty')}`,
    ppsBaseAmount: `${t('ppsBaseAmount')} (BTC)`,
    totalNetworkBlocks: `${t('totalNetworkBlocks')}`,
    totalBlocksUsed: `${t('totalBlocksUsed')}`,
    fppsRatePercent: `${t('fppsRate')} (%)`,
    txFeeRewardAmount: `${t('txFeeReward')} (BTC)`,
    grossEarnings: `${t('grossEarnings')} (BTC)`,
    poolFeeAmount: `${t('poolFeeAmount')} (BTC)`,
    earned: `${t('totalAmount')} (BTC)`
  });
  const [isScrolling, setIsScrolling] = useState(false);
  const [visibleRange, setVisibleRange] = useState({
    startIndex: 0,
    endIndex: 0
  });
  const virtuoso = useRef(null);

  const displayDifficultyChangeInfo = (row, display) => {
    setSelectedRow(row);
    setShowDifficultyChangeInfo(display);
  };

  const handleOpenFppsCalculationDialog = date => {
    const formattedDate = date.split(' ')[0];
    setSelectedFppsDetailsDate(
      dayjs(formattedDate)
        .utc()
        .startOf('day')
    );
    setOpenFppsCalcDialog(true);
  };

  const handleCloseFppsCalculationDialog = date => {
    setSelectedFppsDetailsDate(null);
    setOpenFppsCalcDialog(false);
    setSelectedFppsDetailsDate(null);
  };

  // Historical halving dates (accurate)
  const HALVING_EVENTS = [
    { date: new Date('2009-01-03'), height: 0, subsidy: 50 },
    { date: new Date('2012-11-28'), height: 210000, subsidy: 25 },
    { date: new Date('2016-07-09'), height: 420000, subsidy: 12.5 },
    { date: new Date('2020-05-11'), height: 630000, subsidy: 6.25 },
    { date: new Date('2024-04-20'), height: 840000, subsidy: 3.125 },
    { date: new Date('2028-04-01'), height: 1050000, subsidy: 1.5625 }, // estimated
    { date: new Date('2032-03-15'), height: 1260000, subsidy: 0.78125 } // estimated
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleColumnSort = tableColumn => {
    const isAsc = orderBy === tableColumn && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(tableColumn);
  };

  const handleMobileCardsSort = (tableColumn, columnOrder) => {
    setOrder(columnOrder);
    setOrderBy(tableColumn);
  };

  const getSubsidyFromLookup = date => {
    const auditDate = new Date(date.split(' ')[0]);
    // Find the most recent halving before the given date
    for (let i = HALVING_EVENTS.length - 1; i >= 0; i--) {
      if (auditDate >= HALVING_EVENTS[i].date) {
        return HALVING_EVENTS[i].subsidy;
      }
    }
    return 50; // Before genesis (shouldn't happen)
  };

  const formatHashrate = hashrate => {
    const hashrateUnit = getHashrateUnit(hashrate, HASHRATE.GHS);
    return hashrateUnit.formatToHashrateString(
      (hashrate * HASHRATE.GHS.one).toFixed(3)
    );
  };

  const handleUnitCoversion = (hashrate, hashrateUnit) => {
    switch (hashrateUnit) {
      case 'GH/s':
        return 1000000000 * hashrate;
      case 'TH/s':
        return 1000000000000 * hashrate;
      case 'PH/s':
        return 1000000000000000 * hashrate;
      case 'EH/s':
        return 1000000000000000000 * hashrate;
      default:
        break;
    }
  };

  const handleAggShareDiffCalc = hashrate => {
    const hashrateValue = +hashrate.split(' ')[0];
    const hashrateUnit = hashrate.split(' ')[1];

    const convertedHashrate = handleUnitCoversion(hashrateValue, hashrateUnit);
    return {
      aggregatedDifficulty: (convertedHashrate / 2 ** 32) * 86400,
      formattedAggregatedDifficulty: (
        (convertedHashrate / 2 ** 32) *
        86400
      ).toLocaleString('en-US', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      })
    };
  };

  const fetchData = useCallback(async () => {
    if (subAccounts.length) {
      const { tokenAvailable, accessToken } = await getAccessToken();
      if (!tokenAvailable) {
        console.error(accessToken);
        return;
      }

      setLoading(true);
      Promise.all([
        GetSubAccountFinancialsReport(
          startDate,
          endDate,
          subAccounts,
          'byDay',
          accessToken
        ),
        axios.get(
          `https://mempool.reddfive.com/api/v1/mining/difficulty-adjustments/`
        ),
        GetDailyBlockCounts(
          dayjs(startDate)
            .utc()
            .valueOf(),
          dayjs(endDate)
            .utc()
            .valueOf(),
          accessToken
        )
      ])
        .then(([financials, difficultyData, { data: blocksData }]) => {
          // Pre-process and sort difficulty data
          const sortedDifficulties = !!difficultyData.data?.length
            ? difficultyData.data
                .map(difficulty => ({
                  timestamp: difficulty[0] * 1000, // Convert to milliseconds
                  date: dayjs.unix(difficulty[0]).format('YYYY-MM-DD'),
                  difficulty: difficulty[2]
                }))
                .sort((a, b) => b.timestamp - a.timestamp)
            : [];

          const auditData = !!financials.data?.length
            ? financials.data?.map((financial, index) => {
                const convertedHashrate = formatHashrate(
                  financial.hashrateAccepted
                );
                const startDateUnixMs = new Date(financial.startDate).getTime();
                const startDate = financial.startDate?.split(' ')[0];

                // Find the most recent difficulty adjustment on or before this date
                const currentDifficulty = sortedDifficulties.find(
                  d => d.timestamp <= startDateUnixMs
                );

                const calculatedNetworkDifficulty =
                  currentDifficulty?.difficulty;
                const difficultyChanged =
                  !!sortedDifficulties?.length &&
                  sortedDifficulties.map(d => d.date).includes(startDate);
                const aggregatedDifficultyInfo = handleAggShareDiffCalc(
                  convertedHashrate
                );
                const blocksUsedForFpps = dayjs(financial.startDate).isBefore(
                  dayjs('2023/11/30')
                )
                  ? blocksData[startDate]
                  : blocksData[startDate] - 6;

                return {
                  ...financial,
                  year: Number(
                    dayjs(financial.startDate)
                      .get('year')
                      .toString()
                      .slice(2)
                  ), // display last two digits of year
                  aggregateShareDifficulty:
                    convertedHashrate === '0 H/s'
                      ? 0
                      : aggregatedDifficultyInfo.aggregatedDifficulty,
                  formattedAggregatedDifficulty:
                    convertedHashrate === '0 H/s'
                      ? Number(0).toFixed(3)
                      : aggregatedDifficultyInfo.formattedAggregatedDifficulty,
                  convertedHashrate: financial.convertedHashrate,
                  networkDifficulty: calculatedNetworkDifficulty,
                  networkDifficultyFormatted: calculatedNetworkDifficulty?.toLocaleString(
                    'en-US',
                    { minimumFractionDigits: 2 }
                  ),
                  networkDifficultyChanged: difficultyChanged,
                  blockSubsidy: getSubsidyFromLookup(financial.startDate),
                  ppsBaseAmount: financial.ppsBaseAmount,
                  totalNetworkBlocks:
                    !!startDate &&
                    !!Object.keys(blocksData).length &&
                    blocksData[startDate],
                  totalBlocksUsed:
                    !!Object.keys(blocksData).length && blocksUsedForFpps,
                  fppsRatePercent: financial.fppsRatePercent,
                  txFeeRewardAmount: financial.txFeeRewardAmount,
                  poolFeeAmount: financial.poolFeeAmount,
                  earned: financial.earned
                };
              })
            : [];

          if (!!auditData.length) {
            const csvExport = auditData.map(acct => {
              return {
                Year: acct.year,
                'Start Date\n(UTC)': dayjs(acct.startDate).format(
                  'YYYY-MM-DD HH:mm:ss'
                ),
                'End Date\n(UTC)': dayjs(acct.endDate).format(
                  'YYYY-MM-DD HH:mm:ss'
                ),
                Group: acct.groupName,
                'Sub-\nAccount': acct.subAccountName,
                'Accepted\nAggregate\nShare\nDifficulty':
                  acct.aggregateShareDifficulty,
                'Hashrate\n(TH/s)': acct.convertedHashrate,
                'Network\nDifficulty': acct.networkDifficulty,
                'PPS Base\n(BTC)': acct.ppsBaseAmount,
                'Total\nNetwork\nBlocks': acct.totalNetworkBlocks,
                'Total\nBlocks\nUsed': acct.totalBlocksUsed,
                'FPPS\nRate\n(%)': `${acct.fppsRatePercent}%`,
                'Tx Fee\nReward\n(BTC)': acct.txFeeRewardAmount,
                'Gross\nEarnings\n(BTC)': acct.grossEarnings,
                'Fee\nAmount\n(BTC)': acct.poolFeeAmount,
                'Total Amount (BTC)': acct.earned
              };
            });
            saveExportData(csvExport);
          }

          setRows(auditData);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error: ', error.message);
          const errorMsg = !!error?.message ? error?.message : error;
          setError(errorMsg);
          setLoading(false);
        });
      setPage(0);
    } else {
      setRows([]);
      setLoading(false);
      setPage(0);
    }
  }, [startDate, endDate, subAccounts.length]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const MobileCard = props => {
    const { card, index } = props;

    return (
      <Card
        variant="outlined"
        sx={{
          margin: '40px 10px',
          backgroundColor: 'var(--neutral-3)',
          borderColor: 'var(--color-border)'
        }}
        key={index}
      >
        <CardHeader
          sx={{ margin: theme.spacing(0.5) }}
          title={<Typography>{card.subAccountName}</Typography>}
          subheader={card.groupName}
        ></CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>Year</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.year}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('startTime')} (UTC)</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.startDate}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('endTime')} (UTC)</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.endDate}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('aggregateShareDifficulty')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.formattedAggregatedDifficulty}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('hashrate')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.hashrate}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('networkDifficulty')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.networkDifficultyFormatted}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('blockSubsidy')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.blockSubsidy}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('ppsBaseAmount')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.ppsBaseAmount}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('totalNetworkBlocks')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>
                    {!!card.totalNetworkBlocks ? card.totalNetworkBlocks : 0}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('totalBlocksUsed')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>
                    {!!card.totalBlocksUsed ? card.totalBlocksUsed : 0}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>Details</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography
                    className={classes.calculationInfo}
                    style={mediumDevice ? { fontSize: '1rem' } : {}}
                    onClick={() =>
                      handleOpenFppsCalculationDialog(card.startDate)
                    }
                  >
                    View
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('fppsRate')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.fppsRatePercent}%</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('txFeeReward')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.txFeeRewardAmount}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('grossEarnings')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.grossEarnings}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('poolFeeAmount')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>{card.poolFeeAmount}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Typography>{t('totalAmount')}</Typography>
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Typography>
                    {parseFloat(card.earned).toLocaleString(undefined, {
                      minimumFractionDigits: 8
                    })}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Card
        style={{
          backgroundColor: 'var(--neutral-2)'
        }}
      >
        <>
          {mediumDevice ? (
            <>
              <Box px={1} py={2}>
                <Box>
                  <TableSortControls
                    selectorValues={selectorValues}
                    onChange={handleMobileCardsSort}
                    defaultSort="startDate"
                    defaultOrder="desc"
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent={'flex-end'}
                  alignItems="center"
                  sx={{ marginTop: theme.spacing(3) }}
                >
                  <Typography align="right">
                    Total Items: {sortedRows.length}
                  </Typography>
                </Box>
              </Box>
              {loading ? (
                <Card>
                  <CardContent
                    align="center"
                    style={{ backgroundColor: 'var(--neutral-2)' }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        width: '100%',
                        margin: '20px 0',
                        backgroundColor: 'var(--neutral-3)',
                        borderColor: 'var(--color-border)'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '20px'
                        }}
                      >
                        <CardHeader
                          title={
                            <Skeleton
                              height={24}
                              width={200}
                              variant="rounded"
                              sx={{
                                backgroundColor: 'var(--neutral-7)',
                                border: '1px solid var(--color-border)'
                              }}
                            />
                          }
                        />
                        <CardHeader
                          style={{ width: '100%' }}
                          align="right"
                          title={
                            <Skeleton
                              height={24}
                              width={'20%'}
                              variant="rounded"
                              sx={{
                                backgroundColor: 'var(--neutral-7)',
                                border: '1px solid var(--color-border)'
                              }}
                            />
                          }
                        />
                      </Box>
                      {[...Array(15)].map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}
                        >
                          <CardHeader
                            style={{
                              marginLeft: 16,
                              paddingLeft: 0,
                              borderBottom: '1px solid var(--color-border)'
                            }}
                            title={
                              <Skeleton
                                height={24}
                                width={200}
                                variant="rounded"
                                sx={{
                                  backgroundColor: 'var(--neutral-7)',
                                  border: '1px solid var(--color-border)'
                                }}
                              />
                            }
                          />
                          <CardHeader
                            style={{
                              marginRight: 16,
                              paddingRight: 0,
                              borderBottom: '1px solid var(--color-border)',
                              width: '100%'
                            }}
                            align="right"
                            title={
                              <Skeleton
                                height={24}
                                width={'30%'}
                                variant="rounded"
                                sx={{
                                  backgroundColor: 'var(--neutral-7)',
                                  border: '1px solid var(--color-border)'
                                }}
                              />
                            }
                          />
                        </Box>
                      ))}
                    </Card>
                  </CardContent>
                </Card>
              ) : !loading && sortedRows?.length > 0 ? (
                <div
                  style={{
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <button
                    style={
                      visibleRange.startIndex === 0
                        ? { display: 'none' }
                        : {
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignSelf: 'center',
                            top: '0',
                            opacity: isScrolling ? 0.3 : 1
                          }
                    }
                    onClick={() => {
                      virtuoso.current.scrollToIndex({
                        index: 0,
                        align: 'start',
                        behavior: 'smooth'
                      });
                      return false;
                    }}
                    className={classes.topScrollButton}
                  >
                    <ScrollArrowsUp color={'white'} />
                  </button>
                  <button
                    style={
                      visibleRange.endIndex === sortedRows.length - 1
                        ? { display: 'none' }
                        : {
                            whiteSpace: 'nowrap',
                            opacity: isScrolling ? 0.3 : 1
                          }
                    }
                    onClick={() => {
                      virtuoso.current.scrollToIndex({
                        index: sortedRows.length + 1,
                        align: 'start',
                        behavior: 'smooth'
                      });
                      return false;
                    }}
                    className={classes.bottomScrollButton}
                  >
                    <ScrollArrowsDown color={'white'} />
                  </button>
                  <Virtuoso
                    context={{ isScrolling }}
                    ref={virtuoso}
                    rangeChanged={setVisibleRange}
                    isScrolling={setIsScrolling}
                    style={{
                      height: 'calc(100vh - 216px)',
                      margin: '8px 0 24px'
                    }}
                    totalCount={sortedRows.length}
                    itemContent={index => {
                      const row = sortedRows[index];
                      return <MobileCard card={row} index={`${index}-card`} />;
                    }}
                  />
                  {isScrolling ? (
                    <Card
                      style={{
                        opacity: '0.6',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <CardContent align="center">
                        {' '}
                        <CircularProgress />
                      </CardContent>
                    </Card>
                  ) : null}
                </div>
              ) : !loading && !error && sortedRows.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyItems="center"
                  alignItems="center"
                  style={{ background: 'var(--color-background)' }}
                >
                  <div style={{ position: 'relative' }}>
                    <p
                      style={{
                        position: 'absolute',
                        left: 160,
                        top: 280,
                        fontFamily: 'var(--font-family)',
                        fontSize: 16,
                        fontWeight: 600,
                        color: '#e6e5e8'
                      }}
                    >
                      No audit data yet
                    </p>
                    <p
                      style={{
                        position: 'absolute',
                        left: 150,
                        top: 310,
                        fontFamily: 'var(--font-family)',
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#9AA4B2'
                      }}
                    >
                      This will get populated later!
                    </p>
                    <div
                      style={{
                        position: 'absolute',
                        width: 48,
                        height: 48,
                        left: 217,
                        top: 217,
                        border: '1px solid #364152',
                        borderRadius: 'var(--radius-l)'
                      }}
                    ></div>
                    <svg
                      viewBox="0 0 25 24"
                      style={{ position: 'absolute', left: 229, top: 230 }}
                      width="25"
                      height="24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.5 21L18.0001 17.5M20.5 11.5C20.5 16.1944 16.6944 20 12 20C7.30558 20 3.5 16.1944 3.5 11.5C3.5 6.80558 7.30558 3 12 3C16.6944 3 20.5 6.80558 20.5 11.5Z"
                        stroke="#CDD5DF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <svg
                      width="480"
                      height="480"
                      viewBox="0 0 480 480"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="240" cy="240" r="47.5" stroke="#202939" />
                      <circle cx="240" cy="240" r="79.5" stroke="#202939" />
                      <circle cx="240" cy="240" r="111.5" stroke="#202939" />
                      <circle
                        cx="240"
                        cy="240"
                        r="143.5"
                        stroke="#202939"
                        opacity={0.5}
                      />
                      <circle
                        cx="240"
                        cy="240"
                        r="143.5"
                        stroke="#202939"
                        opacity={0.4}
                      />
                      <circle
                        cx="240"
                        cy="240"
                        r="175.5"
                        stroke="#202939"
                        opacity={0.3}
                      />
                      <circle
                        cx="240"
                        cy="240"
                        r="207.5"
                        stroke="#202939"
                        opacity={0.2}
                      />
                      <circle
                        cx="240"
                        cy="240"
                        r="239.5"
                        stroke="#202939"
                        opacity={0.1}
                      />
                    </svg>
                  </div>
                </Box>
              ) : !loading && error ? (
                <Card
                  variant="outlined"
                  sx={{
                    margin: '20px',
                    backgroundColor: 'var(--neutral-3)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    {error}
                  </CardContent>
                </Card>
              ) : null}
            </>
          ) : (
            <>
              <TableContainer
                style={{
                  backgroundColor: 'var(--neutral-2)',
                  borderTopRightRadius: 0,
                  borderTopLeftRadius: 0,
                  borderTop: '2px solid var(--color-border)',
                  position: 'relative',
                  maxHeight: 'calc(100vh - 400px)'
                }}
                component={Paper}
              >
                <Table className={classes.table} size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      style={{
                        height: '50px',
                        whiteSpace: 'nowrap',
                        backgroundColor: 'var(--color-surface-raised)'
                      }}
                    >
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'year'}
                          direction={orderBy === 'year' ? order : 'asc'}
                          onClick={() => handleColumnSort('year')}
                        >
                          Year
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'startDate'}
                          direction={orderBy === 'startDate' ? order : 'asc'}
                          onClick={() => handleColumnSort('startDate')}
                        >
                          {t('startTime')} (UTC)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'endDate'}
                          direction={orderBy === 'endDate' ? order : 'asc'}
                          onClick={() => handleColumnSort('endDate')}
                        >
                          {t('endTime')} (UTC)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'groupName'}
                          direction={orderBy === 'groupName' ? order : 'asc'}
                          onClick={() => handleColumnSort('groupName')}
                        >
                          {t('group')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                        sortDirection={orderBy === 'subAccount' ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === 'subAccountName'}
                          direction={
                            orderBy === 'subAccountName' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('subAccountName')}
                        >
                          {t('subAccount')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'aggregateShareDifficulty'}
                          direction={
                            orderBy === 'aggregateShareDifficulty'
                              ? order
                              : 'asc'
                          }
                          onClick={() =>
                            handleColumnSort('aggregateShareDifficulty')
                          }
                        >
                          {t('aggregateShareDifficulty')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'convertedHashrate'}
                          direction={
                            orderBy === 'convertedHashrate' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('convertedHashrate')}
                        >
                          {t('hashrate')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'networkDifficulty'}
                          direction={
                            orderBy === 'networkDifficulty' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('networkDifficulty')}
                        >
                          {t('networkDifficulty')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'blockSubsidy'}
                          direction={orderBy === 'blockSubsidy' ? order : 'asc'}
                          onClick={() => handleColumnSort('blockSubsidy')}
                        >
                          {t('blockSubsidy')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'ppsBaseAmount'}
                          direction={
                            orderBy === 'ppsBaseAmount' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('ppsBaseAmount')}
                        >
                          {t('ppsBaseAmount')} (BTC)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'totalNetworkBlocks'}
                          direction={
                            orderBy === 'totalNetworkBlocks' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('totalNetworkBlocks')}
                        >
                          {t('totalNetworkBlocks')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'totalBlocksUsed'}
                          direction={
                            orderBy === 'totalBlocksUsed' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('totalBlocksUsed')}
                        >
                          {t('totalBlocksUsed')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        Details
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'fppsRatePercent'}
                          direction={
                            orderBy === 'fppsRatePercent' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('fppsRatePercent')}
                        >
                          {t('fppsRate')} (%)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'txFeeRewardAmount'}
                          direction={
                            orderBy === 'txFeeRewardAmount' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('txFeeRewardAmount')}
                        >
                          {t('txFeeReward')} (BTC)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'grossEarnings'}
                          direction={
                            orderBy === 'grossEarnings' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('grossEarnings')}
                        >
                          {t('grossEarnings')} (BTC)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'poolFeeAmount'}
                          direction={
                            orderBy === 'poolFeeAmount' ? order : 'asc'
                          }
                          onClick={() => handleColumnSort('poolFeeAmount')}
                        >
                          {t('poolFeeAmount')} (BTC)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: 'var(--color-surface-raised)'
                        }}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === 'earned'}
                          direction={orderBy === 'earned' ? order : 'asc'}
                          onClick={() => handleColumnSort('earned')}
                        >
                          {t('totalAmount')} (BTC)
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && sortedRows?.length > 0 ? (
                      (rowsPerPage > 0
                        ? sortedRows.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                        : sortedRows
                      ).map((row, index) => {
                        return (
                          <TableRow key={index} style={{ height: '50px' }}>
                            <TableCell
                              align="left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {row.year}
                            </TableCell>
                            <TableCell
                              align="left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {row.startDate}
                            </TableCell>
                            <TableCell
                              align="left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {row.endDate}
                            </TableCell>
                            <TableCell
                              align="left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {row.groupName}
                            </TableCell>
                            <TableCell
                              align="left"
                              style={{ whiteSpace: 'nowrap' }}
                              data-testid={`fdr_cel_trust_center_audit_table_sub_account_${index}`}
                            >
                              {row.subAccountName}
                            </TableCell>
                            <TableCell align="left">
                              {row.formattedAggregatedDifficulty}
                            </TableCell>
                            <TableCell align="left">{row.hashrate}</TableCell>
                            <TableCell align="left">
                              {row.networkDifficultyFormatted}
                            </TableCell>
                            <TableCell align="left">
                              {row.blockSubsidy}
                            </TableCell>
                            <TableCell align="left">
                              {row.ppsBaseAmount}
                            </TableCell>
                            <TableCell align="left">
                              {!!row.totalNetworkBlocks
                                ? row.totalNetworkBlocks
                                : 0}
                            </TableCell>
                            <TableCell align="left">
                              {!!row.totalBlocksUsed ? row.totalBlocksUsed : 0}
                            </TableCell>
                            <TableCell align="left">
                              <Typography
                                className={classes.calculationInfo}
                                onClick={() =>
                                  handleOpenFppsCalculationDialog(row.startDate)
                                }
                                data-testid={`fdr_cel_trust_center_audit_table_view_${index}`}
                              >
                                View
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              {row.fppsRatePercent}%
                            </TableCell>
                            <TableCell align="left">
                              {row.txFeeRewardAmount}
                            </TableCell>
                            <TableCell align="left">
                              {row.grossEarnings}
                            </TableCell>
                            <TableCell align="left">
                              {row.poolFeeAmount}
                            </TableCell>
                            <TableCell align="left">
                              {parseFloat(row.earned).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 8 }
                              )}
                              {!!row.networkDifficultyChanged && (
                                <ClickAwayListener
                                  onClickAway={() => {
                                    if (
                                      showDifficultyChangeInfo &&
                                      selectedRow === index
                                    ) {
                                      displayDifficultyChangeInfo(index, false);
                                    }
                                  }}
                                >
                                  <Tooltip
                                    placement="top"
                                    disableFocusListener
                                    disableHoverListener
                                    title={
                                      <Typography
                                        variant="h6"
                                        style={{
                                          padding: `${theme.spacing(
                                            1.0
                                          )} 0 ${theme.spacing(
                                            1.0
                                          )} ${theme.spacing(1.0)}`
                                        }}
                                      >
                                        {t('networkDifficultyChanged')}
                                      </Typography>
                                    }
                                    open={
                                      selectedRow === index &&
                                      showDifficultyChangeInfo &&
                                      !loading
                                    }
                                    arrow
                                  >
                                    <Info
                                      size={16}
                                      fontSize="small"
                                      style={{
                                        marginBottom: theme.spacing(-0.5),
                                        marginLeft: theme.spacing(4.0),
                                        cursor: 'pointer',
                                        color: 'var(--color-text-warning)'
                                      }}
                                      onClick={() => {
                                        displayDifficultyChangeInfo(
                                          index,
                                          !showDifficultyChangeInfo
                                        );
                                      }}
                                    />
                                  </Tooltip>
                                </ClickAwayListener>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : loading ? (
                      <TableRow style={{ height: '50px' }}>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton
                            variant="rounded"
                            height={20}
                            width={'100%'}
                          />
                        </TableCell>
                      </TableRow>
                    ) : !loading && error ? (
                      <TableRow>
                        <TableCell
                          colSpan={18}
                          style={{ height: rowsPerPage * 5 }}
                          align="center"
                        >
                          {error}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableCell colSpan={18} style={{ padding: 0 }}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyItems="center"
                          alignItems="center"
                          style={{ background: 'var(--color-background)' }}
                        >
                          <div style={{ position: 'relative' }}>
                            <p
                              style={{
                                position: 'absolute',
                                left: 160,
                                top: 280,
                                fontFamily: 'var(--font-family)',
                                fontSize: 16,
                                fontWeight: 600,
                                color: '#e6e5e8'
                              }}
                            >
                              No audit data yet
                            </p>
                            <p
                              style={{
                                position: 'absolute',
                                left: 150,
                                top: 310,
                                fontFamily: 'var(--font-family)',
                                fontSize: 14,
                                fontWeight: 400,
                                color: '#9AA4B2'
                              }}
                            >
                              This will get populated later!
                            </p>
                            <div
                              style={{
                                position: 'absolute',
                                width: 48,
                                height: 48,
                                left: 217,
                                top: 217,
                                border: '1px solid #364152',
                                borderRadius: 'var(--radius-l)'
                              }}
                            ></div>
                            <svg
                              viewBox="0 0 25 24"
                              style={{
                                position: 'absolute',
                                left: 229,
                                top: 230
                              }}
                              width="25"
                              height="24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21.5 21L18.0001 17.5M20.5 11.5C20.5 16.1944 16.6944 20 12 20C7.30558 20 3.5 16.1944 3.5 11.5C3.5 6.80558 7.30558 3 12 3C16.6944 3 20.5 6.80558 20.5 11.5Z"
                                stroke="#CDD5DF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <svg
                              width="480"
                              height="480"
                              viewBox="0 0 480 480"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="240"
                                cy="240"
                                r="47.5"
                                stroke="#202939"
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="79.5"
                                stroke="#202939"
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="111.5"
                                stroke="#202939"
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="143.5"
                                stroke="#202939"
                                opacity={0.5}
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="143.5"
                                stroke="#202939"
                                opacity={0.4}
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="175.5"
                                stroke="#202939"
                                opacity={0.3}
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="207.5"
                                stroke="#202939"
                                opacity={0.2}
                              />
                              <circle
                                cx="240"
                                cy="240"
                                r="239.5"
                                stroke="#202939"
                                opacity={0.1}
                              />
                            </svg>
                          </div>
                        </Box>
                      </TableCell>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Table>
                <TableFooter>
                  <TableRow>
                    <TablePaginationComp
                      filteredRows={sortedRows}
                      count={
                        !!sortedRows && !!sortedRows?.length > 0
                          ? sortedRows?.length
                          : 0
                      }
                      rowsPerPage={rowsPerPage}
                      page={page}
                      handleChangePage={handleChangePage}
                      handleChangeRowsPerPage={handleChangeRowsPerPage}
                      disablePagination={
                        loading || error || !sortedRows?.length
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </>
          )}
        </>
      </Card>

      <Dialog
        open={openFppsCalcDialog}
        onClose={handleCloseFppsCalculationDialog}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            style: {
              backgroundColor: 'var(--neutral-2)',
              color: 'var(--color-text)',
              width: mediumDevice ? 'calc(100% - 24px)' : 'initial',
              margin: mediumDevice ? '32px 4px' : '32px'
            }
          }
        }}
      >
        <DialogTitle
          style={{
            borderBottom: '1px solid var(--color-border)',
            padding: '16px 12px'
          }}
        >
          <p
            className="m-heading is-size-xl"
            style={{ color: 'var(--core-white)' }}
          >
            Calculation Breakdown
          </p>
          <div
            style={
              mediumDevice
                ? {
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    cursor: 'pointer'
                  }
                : {
                    position: 'absolute',
                    top: '15px',
                    right: '36px',
                    cursor: 'pointer'
                  }
            }
            onClick={handleCloseFppsCalculationDialog}
            data-testid="fdr_btn_trust_audit_fpps_calc_close"
          >
            <X />
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: '0 12px' }}>
          <FPPSCalculationBreakdown
            date={selectedFppsDetailsDate}
            onDataLoaded={null}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditReportTable;
